import { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { PageHeader, Panel } from "../../components/common";
import { timeAgo } from "../../lib/channels";
import { Copy, CheckCircle2, AlertTriangle, Plug } from "lucide-react";
import { toast } from "sonner";

const apiBase = () => (API.startsWith("http") ? API : `${window.location.origin}${API}`);

function Code({ children }) {
  const copy = () => { navigator.clipboard?.writeText(children); toast.success("Kopyalandı"); };
  return (
    <div className="relative group">
      <pre className="bg-slate-900 text-slate-100 text-xs rounded-lg p-4 overflow-x-auto font-mono whitespace-pre-wrap break-all">{children}</pre>
      <Button size="sm" variant="secondary" onClick={copy} className="absolute top-2 right-2 opacity-80 hover:opacity-100"><Copy className="h-3.5 w-3.5 mr-1.5" />Kopyala</Button>
    </div>
  );
}

export default function Integration() {
  const [ov, setOv] = useState(null);
  useEffect(() => { api.get("/analytics/overview", { params: { days: 7 } }).then((r) => setOv(r.data)); }, []);
  const last = ov?.recent?.[0];
  const connected = last && Date.now() - new Date(last.last_seen).getTime() < 24 * 3600 * 1000;

  return (
    <div className="space-y-6" data-testid="integration-page">
      <PageHeader eyebrow="Sistem" title="Site Entegrasyonu" subtitle="Web sitesindeki izleme betiği, iletişim formu ve bayilik formunun bu panele nasıl bağlandığı." />

      <Panel title="Bağlantı Durumu">
        <div className={`flex items-start gap-3 p-4 rounded-lg border ${connected ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="integration-status">
          {connected ? <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" /> : <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />}
          <div className="text-sm">
            {connected ? (<><b className="text-emerald-900">Site bağlı.</b> Son ziyaretçi verisi {timeAgo(last.last_seen)} alındı.</>)
              : (<><b className="text-amber-900">Henüz ziyaretçi verisi gelmedi.</b> Site dosyalarını (static/js/servisplus-automation.js, iletisim, servisimiz-olun) sunucuya yükleyip siteyi bir kez açın; bu sayfayı yenileyin.</>)}
          </div>
        </div>
        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="p-3 rounded-lg bg-slate-50"><dt className="text-[11px] uppercase tracking-wider text-slate-500">API adresi (sitenin konuşacağı adres)</dt><dd className="font-mono text-xs mt-1 break-all">{apiBase()}</dd></div>
          <div className="p-3 rounded-lg bg-slate-50"><dt className="text-[11px] uppercase tracking-wider text-slate-500">Uçlar</dt><dd className="font-mono text-xs mt-1 leading-relaxed">POST /public/track<br />POST /public/contact<br />POST /public/dealer-application<br />POST /public/review</dd></div>
        </dl>
      </Panel>

      <Panel title="1) Siteye izleme betiği" subtitle="Tüm sayfalarda zaten yüklü olan servisplus-automation.js dosyasının sonuna eklendi — ayrıca bir şey yapmanız gerekmez. API adresi farklıysa aşağıdaki satırı sayfaların <head> kısmına koyun:">
        <Code>{`<script>window.SP_API="${apiBase()}";</script>`}</Code>
        <ul className="mt-4 text-sm text-slate-600 space-y-1.5 list-disc pl-5">
          <li>Her ziyaretçiye rastgele bir oturum kimliği atanır (çerez/IP profili çıkarılmaz).</li>
          <li>Kaydedilenler: sayfa, kaynak (referrer), reklam parametreleri (gclid, utm_*), cihaz/tarayıcı, süre.</li>
          <li>Telefon (tel:) ve WhatsApp bağlantısı tıklamaları “dönüşüm” olarak sayılır.</li>
          <li>Botlar (Googlebot, PageSpeed, AWS tarayıcıları) otomatik elenir; IP’nin son bölümü maskelenir.</li>
          <li><b>Kendi ziyaretlerinizi saymamak için:</b> kullandığınız her tarayıcıda siteyi bir kez <code className="font-mono">?sp_notrack=1</code> ile açın (örn. servisplus.net.tr/?sp_notrack=1). Tekrar saymaya başlamak için <code className="font-mono">?sp_notrack=0</code>.</li>
        </ul>
      </Panel>

      <Panel title="2) Google Ads — reklam trafiğini doğru ayırmak için" subtitle="Otomatik etiketleme (gclid) açıksa Google Ads trafiği zaten otomatik tanınır. Daha detaylı kampanya/anahtar kelime raporu için:">
        <div className="text-sm text-slate-600 mb-2">Google Ads → Hesap Ayarları → Takip → <b>Nihai URL son eki</b> alanına yapıştırın:</div>
        <Code>{`utm_source=google&utm_medium=cpc&utm_campaign={campaignid}&utm_term={keyword}`}</Code>
      </Panel>

      <Panel title="3) Formlar">
        <ul className="text-sm text-slate-600 space-y-2 list-disc pl-5">
          <li><b>/iletisim/</b> sayfasındaki “Bize Yazın” formu → <b>İletişim Mesajları</b> sayfasına düşer.</li>
          <li><b>/servisimiz-olun/</b> başvuru formu → <b>Bayilik Başvuruları</b> sayfasına düşer.</li>
          <li>Her iki form da ziyaretçinin geldiği kanalı (Google Ads / Organik / Direkt …) mesajla birlikte kaydeder.</li>
          <li>Form API’ye ulaşamazsa eski e-posta (mailto) yöntemine otomatik düşer; başvuru kaybolmaz.</li>
          <li>Spam koruması: gizli tuzak alanı + IP başına hız sınırı.</li>
        </ul>
        <div className="mt-4 flex items-start gap-2 text-xs text-slate-500"><Plug className="h-4 w-4 mt-0.5 shrink-0" />Sunucuda <code className="font-mono">CORS_ORIGINS</code> ayarını site adresiyle sınırlayabilirsiniz (örn. <code className="font-mono">https://servisplus.net.tr,https://www.servisplus.net.tr</code>).</div>
      </Panel>
    </div>
  );
}
